import React, { useState, useRef, useEffect, useCallback } from 'react';
import { chatWithRAG } from '../utils/ragChatbot';
import { resizeImage, isValidImage } from '../utils/imageUtils';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * RAG Chat Component
 * 
 * A simple chat interface for interacting with the RAG chatbot.
 * 
 * Usage:
 * <RAGChat />
 * 
 * Or in a modal:
 * <Modal>
 *   <RAGChat />
 * </Modal>
 */
const RAGChat = ({
  onClose,
  className = '',
  darkMode = false,
  hideHeader = false,
  initialMessage = 'Hello there,\nHow can I help you?',
  expanded: expandedProp,
  onExpandChange,
}) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]); // Array of {file, preview, base64}
  const [suggestedPrompts, setSuggestedPrompts] = useState([
    "What's in my course syllabus?",
    'When is my next assignment due?',
    'What courses am I enrolled in?',
    'Show me my recent course materials',
    'Help me understand this topic from my readings',
    'What are my upcoming deadlines?',
  ]);
  const messagesEndRef = useRef(null);
  const containerRef = useRef(null);
  const fileInputRef = useRef(null);
  const [internalExpanded, setInternalExpanded] = useState(false);

  const isExpandedControlled = typeof expandedProp === 'boolean';
  const expanded = isExpandedControlled ? expandedProp : internalExpanded;

  const setExpanded = useCallback(
    (value) => {
      if (!isExpandedControlled) {
        setInternalExpanded(value);
      }
      if (typeof onExpandChange === 'function') {
        onExpandChange(value);
      }
    },
    [isExpandedControlled, onExpandChange]
  );

  const stripDebugComments = useCallback((text) => {
    if (!text) return '';
    return text.replace(/<!--COPILOT_DEBUG[\s\S]*?-->/g, '').trim();
  }, []);

  // update suggested prompts based on recent conversation (very lightweight heuristic)
  useEffect(() => {
    const lastUser = [...messages].reverse().find((m) => m.role === 'user');
    if (!lastUser) return;
    const txt = (lastUser.content || '').toLowerCase();
    const updated = [...suggestedPrompts];
    // Promote relevant prompts to the front
    if (txt.includes('course') || txt.includes('courses')) {
      updated.unshift('What are my courses?');
    } else if (txt.includes('deadline') || txt.includes('due')) {
      updated.unshift('When are my deadlines?');
    } else if (txt.includes('assignment') || txt.includes('homework')) {
      updated.unshift('Can you help me with an assignment?');
    } else if (txt.includes('progress') || txt.includes('score') || txt.includes('grade')) {
      updated.unshift("What's my overall progress?");
    }
    // keep unique and limit to 6
    const unique = [...new Set(updated)].slice(0, 6);
  }, [messages, suggestedPrompts]);

  // No status checking needed - chatbot always ready

  // Convert [[LINK::text::url]] format to markdown links
  const convertCustomLinks = useCallback((text) => {
    if (!text) return text;
    // Replace [[LINK::Link Text::/path]] with [Link Text](/path)
    return text.replace(/\[\[LINK::(.*?)::(.*?)\]\]/g, '[$1]($2)');
  }, []);

  const renderMessageContent = useCallback((message) => {
    if (message.role === 'assistant') {
      const contentWithLinks = convertCustomLinks(message.content || '');
      
      return (
        <div className={`markdown-body ${darkMode ? 'markdown-body--dark' : ''}`}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              a: ({node, children, ...props}) => (
                <a 
                  {...props} 
                  className="text-[#AC5757] hover:underline font-medium"
                  onClick={(e) => {
                    // Internal links should use client-side navigation
                    if (props.href && props.href.startsWith('/')) {
                      e.preventDefault();
                      window.location.href = props.href;
                    }
                  }}
                  target={props.href && props.href.startsWith('http') ? "_blank" : undefined}
                  rel={props.href && props.href.startsWith('http') ? "noopener noreferrer" : undefined}
                >
                  {children && React.Children.count(children) > 0 ? children : props.href}
                </a>
              ),
            }}
          >
            {contentWithLinks}
          </ReactMarkdown>
        </div>
      );
    }
    // User message with optional images
    return (
      <div>
        {message.images && message.images.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {message.images.map((imgSrc, idx) => (
              <img 
                key={idx}
                src={imgSrc} 
                alt={`Uploaded ${idx + 1}`}
                className="max-w-xs max-h-48 rounded border"
              />
            ))}
          </div>
        )}
        <p className="whitespace-pre-wrap">{message.content}</p>
      </div>
    );
  }, [darkMode]);

  // Do not auto-scroll to bottom when new messages arrive (user requested no scroll animations)

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter(isValidImage);
    
    if (validFiles.length === 0) {
      alert('Please upload valid image files (JPEG, PNG, GIF, WebP)');
      return;
    }

    try {
      const imageData = await Promise.all(
        validFiles.map(async (file) => ({
          file,
          preview: URL.createObjectURL(file),
          base64: await resizeImage(file),
        }))
      );
      setUploadedImages([...uploadedImages, ...imageData]);
    } catch (error) {
      console.error('Error processing images:', error);
      alert('Failed to process images. Please try again.');
    }
  };

  const removeImage = (index) => {
    const newImages = uploadedImages.filter((_, i) => i !== index);
    setUploadedImages(newImages);
  };

  const handleSend = async (overrideMessage) => {
    const messageToSend = typeof overrideMessage === 'string' && overrideMessage.trim() ? overrideMessage : input;
    if ((!messageToSend.trim() && uploadedImages.length === 0) || loading) return;

    const userMessage = messageToSend || "Analyze this image";
    const imagesToSend = uploadedImages.map(img => img.base64);
    
    // clear input and images only if we used the input value
    if (!overrideMessage) {
      setInput('');
      setUploadedImages([]);
    }
    setLoading(true);

    // Add user message with image previews
    const newMessages = [...messages, { 
      role: 'user', 
      content: userMessage,
      images: uploadedImages.map(img => img.preview)
    }];
    setMessages(newMessages);

    try {
      // Call RAG chatbot with images
      const result = await chatWithRAG(userMessage, [], imagesToSend);

      if (Array.isArray(result.debug) && result.debug.length > 0) {
        result.debug.forEach((entry) => {
          if (!entry || typeof entry !== 'object') return;
          const { tool, ...rest } = entry;
          const title = tool ? `Copilot Debug (${tool})` : 'Copilot Debug';
          if (console.groupCollapsed) {
            console.groupCollapsed(title);
            Object.entries(rest).forEach(([key, value]) => {
              console.log(`${key}:`, value);
            });
            console.groupEnd();
          } else {
            console.log(title, rest);
          }
        });
      }

      const cleanedContent = stripDebugComments(result.response || '');

      // Add assistant response
      const responseMessage = {
        role: 'assistant',
        content: cleanedContent,
        sources: result.sources,
      };

      setMessages([...newMessages, responseMessage]);
    } catch (error) {
      console.error('Error:', error);
      const errorMessage = {
        role: 'assistant',
        content: `❌ Error: ${error.message}`,
      };
      setMessages([...newMessages, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handlePromptClick = async (prompt) => {
    // send prompt immediately
    try {
      await handleSend(prompt);
    } catch (e) {
      // swallow — handleSend already surfaces errors to chat
    }
  };

  const promptButtonStyle = (dark) => {
    const accent = 'rgba(172,87,87,1)';
    const accentFaint = 'rgba(172,87,87,0.12)';
    const lightBg = 'linear-gradient(180deg, rgba(255,255,255,0.75), rgba(255,255,255,0.70))';
    const darkBg = 'linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01))';
    // Use a two-layer background so the gradient border respects border-radius
    // Plain #AC5757 border without gradient
    const borderColor = '#AC5757';
    const innerBg = dark ? darkBg : lightBg;
    return {
      borderRadius: '18px',
      border: `2px solid ${borderColor}`,
      // inner background only (no gradient border needed)
      background: innerBg,
      // soft outer glow to give a 'pop' feel
      // Light mode: subtle red shadow
      boxShadow: dark
        ? `0 8px 24px rgba(0,0,0,0.6), 0 4px 12px ${accentFaint}`
        : `0 10px 30px rgba(172,87,87,0.12), 0 6px 18px rgba(172,87,87,0.18)`,
      backdropFilter: dark ? 'blur(4px)' : undefined,
      // keep clickable area consistent with existing padding from utility classes
      padding: undefined,
    };
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputFocus = () => {
    setExpanded(true);
  };

  const handleCollapse = () => {
    setExpanded(false);
  };

  return (
    <div
      ref={containerRef}
      className={`relative flex flex-col h-full ${
        darkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'
      } ${className} ${expanded ? 'shadow-2xl' : ''}`}
      style={{
        transformOrigin: 'bottom',
      }}
    >
      {/* Header */}
      {!hideHeader && (
        <div className={`border-b p-4 ${darkMode ? 'border-gray-800' : 'border-gray-200'}`}>
        <div className="flex items-center justify-between">
          <div>
              <h3 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-black'}`}>
                {['Hello there,', 'How can I help you?'].map((s, i) => (
                  <span key={i} className="block">{s}</span>
                ))}
            </h3>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Ready to chat
            </p>
          </div>
          {onClose && (
            <button
              onClick={onClose}
                className={`${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
            >
              ✕
            </button>
          )}
        </div>
      </div>
      )}

      {/* Status banner removed - chatbot always ready */}

      {/* Suggested prompts (click to send) */}
      {suggestedPrompts && suggestedPrompts.length > 0 && (
        <div className={`px-4 ${expanded ? 'py-3' : 'pt-3 pb-0'}`}>
          <div className="flex flex-wrap gap-2">
            {suggestedPrompts.map((p, idx) => (
              <button
                key={idx}
                onClick={() => handlePromptClick(p)}
                style={promptButtonStyle(darkMode)}
                className={`text-sm px-3 py-2 rounded-full transition-transform transform hover:-translate-y-0.5 active:translate-y-0.5 whitespace-nowrap ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`flex ${
              message.role === 'user' ? 'justify-end' : 'justify-start'
            }`}
          >
            <div
              className={`max-w-[80%] rounded-lg p-3 ${
                message.role === 'user'
                  ? 'bg-[#AC5757] text-white'
                  : darkMode
                    ? 'bg-gray-800 text-white border border-gray-700'
                    : 'bg-gray-100 text-gray-900'
              }`}
            >
              {renderMessageContent(message)}
              
              {/* Show sources if available */}
              {message.sources && message.sources.length > 0 && (
                <div className={`mt-2 pt-2 border-t ${darkMode ? 'border-gray-700' : 'border-gray-300'}`}>
                  <p className="text-xs font-semibold mb-1">Sources:</p>
                  {message.sources.map((source, idx) => (
                    <p key={idx} className="text-xs opacity-80">
                      📄 {source.file} (page {source.page})
                    </p>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        
        {loading && (
          <div className="flex justify-start">
            <div className={`${darkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'} rounded-lg p-3`}>
              <p>
                Thinking...
              </p>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className={`${expanded ? 'p-4' : 'px-4 pb-4 pt-2'}`}>
        {/* Image Previews */}
        {uploadedImages.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-2">
            {uploadedImages.map((img, idx) => (
              <div key={idx} className="relative">
                <img 
                  src={img.preview} 
                  alt={`Upload ${idx + 1}`}
                  className="h-20 w-20 object-cover rounded border"
                />
                <button
                  onClick={() => removeImage(idx)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
        
        <div className="flex space-x-2">
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageUpload}
            className="hidden"
          />
          
          {/* Image upload button */}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={loading}
            className={`px-3 py-2 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed ${
              darkMode
                ? 'border border-gray-700 bg-gray-800 text-gray-300 hover:bg-gray-700'
                : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            }`}
            title="Upload images for analysis"
          >
            📷
          </button>
          
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onFocus={handleInputFocus}
            onKeyPress={handleKeyPress}
            placeholder="Ask me anything about your courses, challenges, or concepts..."
            className={`flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
              darkMode
                ? 'border-gray-700 bg-gray-800 text-white focus:ring-[#AC5757]'
                : 'border-gray-300 bg-white text-gray-900 focus:ring-[#AC5757]'
            }`}
          disabled={loading}
          />
          <button
            onClick={handleSend}
          disabled={loading || (!input.trim() && uploadedImages.length === 0)}
            className={`px-6 py-2 rounded-lg font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed shadow-lg border-2 ${
              darkMode 
                ? 'border-[#AC5757] bg-transparent text-[#AC5757] hover:bg-[#AC5757] hover:text-white' 
                : 'border-[#AC5757] bg-transparent text-[#AC5757] hover:bg-[#AC5757] hover:text-white'
            }`}
          >
            {loading ? '...' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RAGChat;





